import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { RefreshCw } from 'lucide-react';
import { useAuth } from '../../context/AuthContext.js';
import Alert from '../common/Alert.js';
import Button from '../common/Button.js';
import Loader from '../common/Loader.js';
import { listIdLinkMobileClass } from '../common/uiClasses.js';
import ogclTransferService from '../../services/ogclTransferService.js';
import OgclUnloadNoteModal from './OgclUnloadNoteModal.js';
import OgclShipmentActionsMenu from './OgclShipmentActionsMenu.js';
import { isPendingUnloadRow, isShipmentUnloaded, shipmentOnMlSheet } from './itclShipmentActions.js';

const UNLOAD_STATUS_FILTERS = [
  { id: 'pending', label: 'Pending' },
  { id: 'unloaded', label: 'Unloaded' },
];

const unloadStatusClass = (row) => {
  if (shipmentOnMlSheet(row)) return 'bg-gray-50 text-gray-700 border-gray-200';
  if (isShipmentUnloaded(row)) return 'bg-green-50 text-green-700 border-green-200';
  return 'bg-amber-50 text-amber-800 border-amber-200';
};

const unloadStatusLabel = (row) => {
  if (shipmentOnMlSheet(row)) return 'On ML sheet';
  if (isShipmentUnloaded(row)) return 'Unloaded';
  return 'Pending';
};

const pendingIncomingCount = (list) => (Array.isArray(list) ? list : []).filter(isPendingUnloadRow).length;

const OgclUnloadPanel = ({ onCountChange }) => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const role = user?.role || user?.role_name;
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('pending');
  const [selectedIds, setSelectedIds] = useState([]);
  const [acting, setActing] = useState(false);
  const [openMenuId, setOpenMenuId] = useState(null);
  const [noteModal, setNoteModal] = useState({ open: false, action: 'unload', shipmentIds: [] });
  const hasLoadedRef = useRef(false);

  const onCountChangeRef = useRef(onCountChange);
  onCountChangeRef.current = onCountChange;

  const fetchRows = useCallback(async () => {
    const isRefresh = hasLoadedRef.current;
    try {
      if (isRefresh) setRefreshing(true);
      else setLoading(true);
      const response = await ogclTransferService.incomingShipments();
      let list = response.data?.data || [];
      let next = Array.isArray(list) ? list : [];
      if (!next.length) {
        const incomingReqs = await ogclTransferService.list({ incoming: true });
        const reqList = incomingReqs.data?.data || incomingReqs.data || [];
        const approved = (Array.isArray(reqList) ? reqList : []).filter(
          (row) => String(row.status || '').toUpperCase() === 'APPROVED',
        );
        const details = await Promise.all(
          approved.map((row) => ogclTransferService.get(row.request_id).catch(() => null)),
        );
        next = details.flatMap((res) => {
          const data = res?.data?.data || res?.data;
          if (!data?.request_id) return [];
          return (data.shipments || []).map((shipment) => ({
            ...shipment,
            request_id: data.request_id,
            from_hub_name: data.from_hub_name,
            to_hub_name: data.to_hub_name,
            booked_by: data.requested_by_display || data.requested_by_name,
          }));
        });
      }
      setRows(next);
      onCountChangeRef.current?.(pendingIncomingCount(next));
    } catch (error) {
      Alert.error(error.response?.data?.error || 'Failed to load incoming shipments');
      setRows([]);
      onCountChangeRef.current?.(0);
    } finally {
      hasLoadedRef.current = true;
      setLoading(false);
      setRefreshing(false);
    }
  }, []);

  useEffect(() => {
    fetchRows();
  }, [fetchRows]);

  const searchedRows = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return rows;
    return rows.filter((row) => {
      const hay = [
        row.shipment_id,
        row.consignment_no,
        row.from_hub_name,
        row.to_hub_name,
        row.booked_by,
        row.request_id,
        row.unload_note,
        row.sender_name,
        row.receiver_name,
      ]
        .filter(Boolean)
        .join(' ')
        .toLowerCase();
      return hay.includes(q);
    });
  }, [rows, search]);

  const pendingCount = pendingIncomingCount(rows);
  const unloadedCount = Math.max(0, rows.length - pendingCount);

  const filteredRows = useMemo(() => {
    if (statusFilter === 'unloaded') {
      return searchedRows.filter((row) => !isPendingUnloadRow(row));
    }
    return searchedRows.filter(isPendingUnloadRow);
  }, [searchedRows, statusFilter]);

  const pendingRows = filteredRows.filter(isPendingUnloadRow);
  const pendingIds = pendingRows.map((row) => row.shipment_id);
  const allPendingSelected = pendingIds.length > 0 && pendingIds.every((id) => selectedIds.includes(id));
  const showPendingList = statusFilter === 'pending';

  const selectedPendingIds = selectedIds.filter((id) => {
    const row = rows.find((item) => item.shipment_id === id);
    return row && isPendingUnloadRow(row);
  });

  const toggleAll = () => {
    if (allPendingSelected) {
      setSelectedIds((prev) => prev.filter((id) => !pendingIds.includes(id)));
      return;
    }
    setSelectedIds((prev) => [...new Set([...prev, ...pendingIds])]);
  };

  const toggleRow = (row) => {
    if (!isPendingUnloadRow(row)) return;
    setSelectedIds((prev) => (
      prev.includes(row.shipment_id)
        ? prev.filter((id) => id !== row.shipment_id)
        : [...prev, row.shipment_id]
    ));
  };

  const openNoteModal = (action, shipmentIds) => {
    const ids = shipmentIds || selectedPendingIds;
    if (!ids.length) {
      Alert.error('Select at least one shipment to unload');
      return;
    }
    setNoteModal({ open: true, action, shipmentIds: ids });
  };

  const closeNoteModal = () => {
    if (acting) return;
    setNoteModal({ open: false, action: 'unload', shipmentIds: [] });
  };

  const confirmNoteAction = async (note) => {
    const ids = noteModal.shipmentIds || [];
    const action = noteModal.action;
    if (!ids.length) {
      Alert.error('Select at least one shipment');
      return;
    }
    if (!String(note || '').trim()) {
      Alert.error('Note is required');
      return;
    }
    try {
      setActing(true);
      if (action === 'revert') {
        await ogclTransferService.undoUnloadBulk({ shipment_ids: ids, note: note.trim() });
        Alert.success(`Reverted ${ids.length} shipment${ids.length === 1 ? '' : 's'} to pending unload`);
      } else if (action === 'remove') {
        await ogclTransferService.revertBulk({ shipment_ids: ids, note: note.trim() });
        Alert.success(`Removed ${ids.length} shipment${ids.length === 1 ? '' : 's'} from the ITCL request`);
      } else if (action === 'note') {
        const byRequest = new Map();
        ids.forEach((id) => {
          const row = rows.find((item) => item.shipment_id === id);
          const requestId = row?.request_id;
          if (!requestId) return;
          if (!byRequest.has(requestId)) byRequest.set(requestId, []);
          byRequest.get(requestId).push(id);
        });
        if (!byRequest.size) {
          Alert.error('This shipment is missing a transfer request id');
          return;
        }
        for (const [requestId, shipmentIds] of byRequest.entries()) {
          await ogclTransferService.note(requestId, {
            shipment_ids: shipmentIds,
            note: note.trim(),
          });
        }
        Alert.success(`Note saved for ${ids.length} shipment${ids.length === 1 ? '' : 's'}`);
      } else {
        await ogclTransferService.unloadBulk({ shipment_ids: ids, note: note.trim() });
        Alert.success(`Unloaded ${ids.length} shipment${ids.length === 1 ? '' : 's'}`);
      }
      setSelectedIds([]);
      setNoteModal({ open: false, action: 'unload', shipmentIds: [] });
      if (action === 'revert') setStatusFilter('pending');
      await fetchRows();
    } catch (error) {
      Alert.error(error.response?.data?.error || 'Failed to update shipments');
    } finally {
      setActing(false);
    }
  };

  const renderActions = (row, menuKey) => (
    <OgclShipmentActionsMenu
      menuKey={menuKey}
      openMenuId={openMenuId}
      setOpenMenuId={setOpenMenuId}
      shipment={row}
      role={role}
      canManage
      acting={acting}
      onView={() => navigate(`/shipments/view/${encodeURIComponent(row.shipment_id)}`)}
      onUnload={() => openNoteModal('unload', [row.shipment_id])}
      onRevert={() => openNoteModal('revert', [row.shipment_id])}
      onRemove={() => openNoteModal('remove', [row.shipment_id])}
      onNote={() => openNoteModal('note', [row.shipment_id])}
    />
  );

  if (loading) {
    return <Loader fullScreen={false} message="Loading incoming shipments..." />;
  }

  return (
    <div className="bg-white rounded-xl border border-gray-100 shadow-sm overflow-hidden">
      <div className="p-3 sm:p-4 border-b border-gray-100">
        <div className="flex items-center gap-2 sm:gap-3 overflow-x-auto">
          <div className="flex shrink-0 gap-1.5 p-1 rounded-xl bg-gray-50 border border-gray-200">
            {UNLOAD_STATUS_FILTERS.map((tab) => {
              const active = statusFilter === tab.id;
              const count = tab.id === 'pending' ? pendingCount : unloadedCount;
              return (
                <button
                  key={tab.id}
                  type="button"
                  onClick={() => setStatusFilter(tab.id)}
                  className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs sm:text-sm font-semibold whitespace-nowrap transition-colors ${
                    active
                      ? 'bg-white text-gray-900 shadow-sm border border-gray-200'
                      : 'text-gray-600 hover:text-gray-900 hover:bg-white/70'
                  }`}
                >
                  {tab.label}
                  <span
                    className={`min-w-[1.25rem] text-center rounded-full px-1.5 py-0.5 text-[10px] font-bold ${
                      active ? 'bg-blue-50 text-blue-700' : 'bg-gray-200/80 text-gray-600'
                    }`}
                  >
                    {count}
                  </span>
                </button>
              );
            })}
          </div>
          <input
            type="search"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="GC number, sender, receiver..."
            className="flex-1 min-w-[12rem] border border-gray-300 rounded-xl px-3 py-2 text-sm"
          />
          <div className="flex shrink-0 items-center gap-2">
            <Button
              variant="reset"
              icon={RefreshCw}
              onClick={fetchRows}
              disabled={loading || refreshing || acting}
              loading={refreshing}
            >
              Refresh
            </Button>
            {showPendingList && (
              <Button
                variant="primary"
                onClick={() => openNoteModal('unload')}
                disabled={!selectedPendingIds.length || acting}
              >
                Unload{selectedPendingIds.length ? ` (${selectedPendingIds.length})` : ''}
              </Button>
            )}
          </div>
        </div>
      </div>
      <p className="px-3 sm:px-4 pb-3 text-xs text-gray-500">
        Pending shows GCs still waiting to be unloaded. After unload they move to the
        Unloaded list. Use Revert on an unloaded GC to send it back to Pending.
        Unload is blocked after an ML sheet.
      </p>

      {filteredRows.length === 0 ? (
        <p className="text-sm text-gray-400 text-center py-10">
          {showPendingList
            ? 'No pending shipments to unload for this hub'
            : 'No unloaded shipments for this hub'}
        </p>
      ) : (
        <>
          <div className="hidden xl:block overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-gray-50 text-gray-600">
                <tr>
                  {showPendingList ? (
                    <th className="px-4 py-2.5 text-left">
                      <input
                        type="checkbox"
                        checked={allPendingSelected}
                        disabled={!pendingIds.length}
                        onChange={toggleAll}
                        aria-label="Select all pending unload"
                      />
                    </th>
                  ) : null}
                  <th className="text-left px-4 py-2.5 font-semibold">GC Number</th>
                  <th className="text-left px-4 py-2.5 font-semibold">CN</th>
                  <th className="text-left px-4 py-2.5 font-semibold">Request</th>
                  <th className="text-left px-4 py-2.5 font-semibold">From → To hub</th>
                  <th className="text-left px-4 py-2.5 font-semibold">Booked by</th>
                  <th className="text-left px-4 py-2.5 font-semibold">Note</th>
                  <th className="text-left px-4 py-2.5 font-semibold">Status</th>
                  <th className="text-center px-4 py-2.5 font-semibold">Action</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {filteredRows.map((row) => (
                    <tr key={row.shipment_id} className="hover:bg-gray-50">
                      {showPendingList ? (
                        <td className="px-4 py-2.5">
                          <input
                            type="checkbox"
                            checked={selectedIds.includes(row.shipment_id)}
                            disabled={!isPendingUnloadRow(row)}
                            onChange={() => toggleRow(row)}
                            aria-label={`Select ${row.shipment_id}`}
                          />
                        </td>
                      ) : null}
                      <td className="px-4 py-2.5 font-medium">
                        <button
                          type="button"
                          className="text-secondary hover:underline"
                          onClick={() => navigate(`/shipments/view/${encodeURIComponent(row.shipment_id)}`)}
                        >
                          {row.shipment_id}
                        </button>
                      </td>
                      <td className="px-4 py-2.5">{row.consignment_no || '—'}</td>
                      <td className="px-4 py-2.5">
                        {row.request_id ? (
                          <button
                            type="button"
                            className="text-secondary hover:underline"
                            onClick={() => navigate(`/ogcl-transfer-requests/view/${encodeURIComponent(row.request_id)}`)}
                          >
                            {row.request_id}
                          </button>
                        ) : '—'}
                      </td>
                      <td className="px-4 py-2.5">
                        {row.from_hub_name || '—'} → {row.to_hub_name || '—'}
                      </td>
                      <td className="px-4 py-2.5">{row.booked_by || '—'}</td>
                      <td className="px-4 py-2.5 text-gray-600 max-w-[220px]">
                        <span className="line-clamp-2">{row.unload_note || '—'}</span>
                      </td>
                      <td className="px-4 py-2.5">
                        <span className={`inline-flex px-2 py-0.5 rounded-full text-xs font-semibold border ${unloadStatusClass(row)}`}>
                          {unloadStatusLabel(row)}
                        </span>
                      </td>
                      <td className="px-4 py-2.5 text-center">
                        {renderActions(row, row.shipment_id)}
                      </td>
                    </tr>
                ))}
              </tbody>
            </table>
          </div>

          <div className="xl:hidden space-y-3 p-3 sm:p-4">
            {showPendingList ? (
              <label className="flex items-center gap-2 text-sm font-medium text-gray-700">
                <input
                  type="checkbox"
                  checked={allPendingSelected}
                  disabled={!pendingIds.length}
                  onChange={toggleAll}
                />
                Select all pending unload
              </label>
            ) : null}
            {filteredRows.map((row) => (
                <div
                  key={`card-${row.shipment_id}`}
                  className="rounded-xl border border-gray-100 border-l-4 border-l-blue-500 p-3"
                >
                  <div className="flex items-start gap-2">
                    {showPendingList ? (
                      <input
                        type="checkbox"
                        className="mt-1"
                        checked={selectedIds.includes(row.shipment_id)}
                        disabled={!isPendingUnloadRow(row)}
                        onChange={() => toggleRow(row)}
                      />
                    ) : null}
                    <div className="min-w-0 flex-1">
                      <div className="flex items-start justify-between gap-2">
                        <button
                          type="button"
                          className={`${listIdLinkMobileClass} text-left`}
                          onClick={() => navigate(`/shipments/view/${encodeURIComponent(row.shipment_id)}`)}
                        >
                          {row.shipment_id}
                        </button>
                        {renderActions(row, `card-${row.shipment_id}`)}
                      </div>
                      <p className="text-xs text-gray-600 mt-1">CN {row.consignment_no || '—'}</p>
                      {row.request_id ? (
                        <button
                          type="button"
                          className="text-xs text-secondary hover:underline mt-1"
                          onClick={() => navigate(`/ogcl-transfer-requests/view/${encodeURIComponent(row.request_id)}`)}
                        >
                          {row.request_id}
                        </button>
                      ) : null}
                      <p className="text-xs text-gray-600 mt-1">
                        {row.from_hub_name || '—'} → {row.to_hub_name || '—'}
                      </p>
                      <p className="text-xs text-gray-500 mt-1">Booked by {row.booked_by || '—'}</p>
                      {row.unload_note ? (
                        <p className="text-xs text-gray-600 mt-1">Note: {row.unload_note}</p>
                      ) : null}
                      <span className={`inline-flex mt-1 px-2 py-0.5 rounded-full text-[10px] font-semibold border ${unloadStatusClass(row)}`}>
                        {unloadStatusLabel(row)}
                      </span>
                    </div>
                  </div>
                </div>
            ))}
          </div>
        </>
      )}

      <OgclUnloadNoteModal
        open={noteModal.open}
        action={noteModal.action}
        shipmentIds={noteModal.shipmentIds}
        loading={acting}
        onClose={closeNoteModal}
        onConfirm={confirmNoteAction}
      />
    </div>
  );
};

export default OgclUnloadPanel;
